import { Component, ElementRef, type OnInit, Renderer2, ViewChild } from '@angular/core'

import { AgGridAngular } from 'ag-grid-angular'
import { type ColDef } from 'ag-grid-community'
import { KpiDetailModalComponent } from 'src/app/04_modals/kpi-detail-modal/kpi-detail-modal.component'
import {
  FullTableModalComponent,
  type FullTableModalData
} from 'src/app/04_modals/full-table-modal/full-table-modal.component'
import { MatDialog } from '@angular/material/dialog'
import { DynamicDialogRef, DialogService } from 'primeng/dynamicdialog'
import { PerformanceDashboardAPIs } from 'src/app/core/apis/performance-dashboard_api-calls'
import { PerformanceDashboardBarchartComponent } from 'src/app/04_modals/performance-dashboard-barchart/performance-dashboard-barchart.component'
import { PerformanceDashboardLinechartComponent } from 'src/app/04_modals/performance-dashboard-linechart/performance-dashboard-linechart.component'
import { DrillInFilteringComponent } from 'src/app/04_modals/size-distro-management/drill-in-filtering/drill-in-filtering.component'
import { DrillInFilteringAPIs } from 'src/app/core/apis/drill-in-filtering_api-calls'
import { GenerateRandomColor } from 'src/app/utils/global-chart-settings'
import { environment } from 'src/environments/environment'

@Component({
  standalone: false,
  selector: 'app-performance-dashboard',
  templateUrl: './performance-dashboard.component.html',
  styleUrl: './performance-dashboard.component.scss'
})
export class PerformanceDashboardComponent implements OnInit {
  @ViewChild('agGrid') agGrid!: AgGridAngular
  exportButton!: HTMLButtonElement
  isExporting = false

  // Type guards to safely narrow Element -> HTMLElement/HTMLButtonElement
  private isHTMLElement (el: Element | null): el is HTMLElement {
    return el instanceof HTMLElement
  }

  private exportButtonContainer: HTMLElement | null = null
  // Bumped on every getPerformanceData() call so a slow, stale response
  // (e.g. from Clear) can't land after a newer one (e.g. from Apply) and
  // silently overwrite the dashboard with out-of-sync data.
  private performanceDataRequestId = 0

  private isHTMLButtonElement (el: Element | null): el is HTMLButtonElement {
    return el instanceof HTMLButtonElement
  }

  constructor (
    private readonly dialog: MatDialog,
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly performanceDashboardAPIs: PerformanceDashboardAPIs,
    private ref: DynamicDialogRef,
    public dialogService: DialogService,
    public drillInFilteringAPIs: DrillInFilteringAPIs
  ) {}

  prod_labels = environment.prod_labels

  loading = true
  dashboardLoading = true
  performanceData: any = null
  pdfButtonVisible = false

  selectedSizeRanges: string[] = ['All']

  // Filter Model
  filters = {
    department: 'All',
    class: '',
    subclass: '',
    style: '',
    styleColor: '',
    channel: '',
    customStoreList: '',
    storeId: '',
    season: '',
    // sizeRange feeds the PDF export's filter summary label; selectedSizeRanges
    // is what f_performance_dashboard_data.sql actually reads to filter the
    // dashboard/KPI data. Both are kept in sync from the same selection.
    sizeRange: ['All'],
    selectedSizeRanges: ['All']
  }

  // Mock data for all dropdowns
  departments: string[] = []
  classes: string[] = []
  subclasses: string[] = []
  styles: string[] = []
  styleColors: string[] = []
  channels: string[] = []
  customStoreLists: string[] = []
  storeIds: string[] = []
  seasons: string[] = []
  sizeRanges: string[] = []

  // // Retrieved from API
  // retrievedClasses: any[] = []
  // retrievedSubclasses: any[] = []
  // retrievedStyleColors: any[] = []
  // retrievedChannels: any[] = []
  // retrievedCustomStoreLists: any[] = []
  // retrievedStoreIds: any[] = []
  // retrievedSeasons: any[] = []
  // retrievedSizeRanges: any[] = []

  // Filtered options for each field
  filteredDepartments: string[] = []
  filteredClasses: string[] = []
  filteredSubclasses: string[] = []
  filteredStyles: string[] = []
  filteredStyleColors: string[] = []
  filteredChannels: string[] = []
  filteredCustomStoreLists: string[] = []
  filteredStoreIds: string[] = []
  filteredSeasons: string[] = []
  filteredSizeRanges: string[] = []

  departmentLoading = false
  classLoading = false
  subclassLoading = false
  styleLoading = false
  channelLoading = false
  customStoreListLoading = false

  // Mock Chart Data
  seasonalLostSalesData: any = {}

  drillInFilters: { send_neg_1, store_list } = {
    send_neg_1: false,
    store_list: { store_list: null, id: null }
  }

  drillInFiltersStoreList: {
    allLists
    listNames
  } = { allLists: [], listNames: [] }

  seasonalLostSalesOptions: any = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.raw}`
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  }

  profilePrecisionData: any = {}
  profilePrecisionOptions = {
    responsive: true,
    interaction: {
      mode: 'nearest',
      intersect: true,
      axis: 'x'
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'start',
        labels: {
          usePointStyle: true,
          font: { size: 12 },
          margin: 20,
          generateLabels (chart) {
            const datasets = chart.data.datasets

            return datasets.map((dataset, index) => {
              const isDashed = dataset.borderDash && dataset.borderDash.length > 0

              return {
                text: dataset.label,
                fillStyle: dataset.borderColor,
                strokeStyle: dataset.borderColor,
                lineWidth: 2,
                hidden: !chart.isDatasetVisible(index),
                datasetIndex: index,
                pointStyle: isDashed ? 'triangle' : 'dotted'
              }
            })
          }
        }
      },
      tooltip: {
        callbacks: {
          title: function (context) {
            const dataset = context[0].dataset
            const sizeRange = dataset.sizeRangeName
            const label = context[0].label
            return `Size: ${label}`
          },
          label: function (context) {
            const dataset = context.dataset
            const value = context.parsed.y
            const type = dataset.dataType === 'profile' ? 'Profile' : 'Demand'
            const sizeRange = dataset.sizeRangeName
            return `${sizeRange} - ${type}: ${value}%`
          }
        }
      }
    },
    scales: {
      x: {
        type: 'category'
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return value + '%'
          }
        }
      }
    }
  }

  // Mock Table Data
  sizeSellThroughColumnDefs: ColDef[] = [
    { headerName: 'Size', field: 'product_size', width: 250 },
    { headerName: 'Sell-Through %', field: 'sell_through_pct', width: 300 },
    { headerName: 'Last Season %', field: 'last_season_pct', width: 400 }
  ]

  sizeSellThroughData: any[] = []

  sizeSellThroughGridOptions: any = {
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true
    },
    suppressContextMenu: true,
    suppressNoRowsOverlay: false,
    rowHeight: 35,
    headerHeight: 40,
    rowStyle: {
      margin: '0px'
    },
    suppressRowVirtualisation: true,
    rowBuffer: 100
  }

  ngOnInit () {
    this.loadDashboardFilters()
    this.getStoreLists()
  }

  async getStoreLists () {
    return await Promise.all([this.drillInFilteringAPIs.GetAllStoreLists()]).then(data => {
      this.drillInFiltersStoreList = data[0]
      console.log('Loaded store lists:', this.drillInFiltersStoreList)
    })
  }

  addExportButton (): void {
    setTimeout(() => {
      const h1 = document.querySelector('.dashboard-header-item')
      if (!h1) return

      const buttonContainer = this.renderer.createElement('div')
      this.exportButtonContainer = buttonContainer
      this.renderer.addClass(buttonContainer, 'export-pdf-btn')
      this.renderer.setStyle(buttonContainer, 'margin-left', 'auto')

      const button = this.renderer.createElement('button')
      this.renderer.setStyle(button, 'background-color', '#376fd0')
      this.renderer.setStyle(button, 'color', 'white')
      this.renderer.setStyle(button, 'padding', '10px 16px')
      this.renderer.setStyle(button, 'border', 'none')
      this.renderer.setStyle(button, 'border-radius', '4px')
      this.renderer.setStyle(button, 'cursor', 'pointer')
      this.renderer.setStyle(button, 'font-size', '14px')
      this.renderer.setStyle(button, 'font-weight', '500')
      this.renderer.setStyle(button, 'display', 'flex')
      this.renderer.setStyle(button, 'align-items', 'center')
      this.renderer.setStyle(button, 'gap', '8px')
      this.renderer.setStyle(
        button,
        'box-shadow',
        '0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12)'
      )

      // Create spinner element (hidden by default)
      const spinner = this.renderer.createElement('span')
      this.renderer.addClass(spinner, 'export-spinner')
      this.renderer.setStyle(spinner, 'display', 'none')
      this.renderer.setStyle(spinner, 'width', '16px')
      this.renderer.setStyle(spinner, 'height', '16px')
      this.renderer.setStyle(spinner, 'border', '2px solid rgba(255, 255, 255, 0.3)')
      this.renderer.setStyle(spinner, 'border-top-color', 'white')
      this.renderer.setStyle(spinner, 'border-radius', '50%')
      this.renderer.setStyle(spinner, 'animation', 'spin 0.6s linear infinite')

      const buttonText = this.renderer.createText('Export to PDF')
      this.renderer.appendChild(button, spinner)
      this.renderer.appendChild(button, buttonText)
      this.renderer.appendChild(buttonContainer, button)

      this.renderer.listen(button, 'click', () => {
        this.exportToPDF(button, spinner, buttonText)
      })

      const h1Parent = h1.parentElement
      if (this.isHTMLElement(h1Parent)) {
        this.renderer.setStyle(h1Parent, 'display', 'flex')
        this.renderer.setStyle(h1Parent, 'align-items', 'center')
        this.renderer.setStyle(h1Parent, 'width', '100%')
        this.renderer.appendChild(h1Parent, buttonContainer)
      }

      // Add keyframes for spinner animation
      if (!document.getElementById('spinner-keyframes')) {
        const style = this.renderer.createElement('style')
        this.renderer.setAttribute(style, 'id', 'spinner-keyframes')
        const css = this.renderer.createText(`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `)
        this.renderer.appendChild(style, css)
        this.renderer.appendChild(document.head, style)
      }
    }, 0)
  }

  clearFilters () {
    this.filters = {
      department: 'All',
      class: '',
      subclass: '',
      style: '',
      styleColor: '',
      channel: '',
      customStoreList: '',
      storeId: '',
      season: this.seasons[this.seasons.length - 1],
      sizeRange: ['All'],
      selectedSizeRanges: ['All']
    }
    // Reset all filtered options
    this.filteredDepartments = [...this.departments]
    this.filteredClasses = []
    this.filteredSubclasses = []
    this.filteredStyles = []
    this.filteredStyleColors = []
    this.filteredChannels = [...this.channels]
    this.filteredCustomStoreLists = [...this.customStoreLists]
    this.filteredStoreIds = []
    this.filteredSeasons = [...this.seasons]
    this.filteredSizeRanges = [...this.sizeRanges]
    this.selectedSizeRanges = ['All']

    this.dashboardLoading = true
    if (this.exportButtonContainer && this.exportButtonContainer.parentNode) {
      this.pdfButtonVisible = false
      this.renderer.removeChild(this.exportButtonContainer.parentNode, this.exportButtonContainer)
      this.exportButtonContainer = null
    }
    this.getPerformanceData()
  }

  // Filter function
  private filterOptions (searchTerm: string, options: string[]): string[] {
    if (!searchTerm) {
      return options
    }
    return options.filter(option => option.toLowerCase().includes(searchTerm.toLowerCase()))
  }

  // Blends a hex color toward white by `amount` (0-1) -- used to give the
  // Demand line a lighter tint of the Profile line's color for the same
  // size range, so the two are distinguishable by color, not just shape/dash.
  private lightenColor (hex: string, amount: number): string {
    const parsed = hex.replace('#', '')
    const num = parseInt(
      parsed.length === 3
        ? parsed
          .split('')
          .map(c => c + c)
          .join('')
        : parsed,
      16
    )
    const r = (num >> 16) & 0xff
    const g = (num >> 8) & 0xff
    const b = num & 0xff
    const blend = (channel: number) => Math.round(channel + (255 - channel) * amount)
    return `rgb(${blend(r)}, ${blend(g)}, ${blend(b)})`
  }

  // Department methods
  onDepartmentInputChange (event: any): void {
    const searchTerm = event.target.value
    this.filteredDepartments = this.filterOptions(searchTerm, this.departments)
  }

  onDepartmentOptionSelected (value: string): void {
    this.filters.department = value
    this.filters.class = ''
    this.filters.subclass = ''
    this.filters.style = ''
    this.filteredClasses = []
    this.filteredSubclasses = []
    this.filteredStyles = []
    this.departmentLoading = true

    this.performanceDashboardAPIs
      .GetLowerPerformanceFilters({ product_id: value })
      .then(data => {
        if (data) {
          console.log('Loaded lower filters:', data)
          this.filteredClasses = data.products.map(product => product.product_id)
          this.classes = this.filteredClasses
        }
      })
      .finally(() => {
        this.departmentLoading = false
      })
  }

  // Class methods
  onClassInputChange (event: any): void {
    const searchTerm = event.target.value
    this.filteredClasses = this.filterOptions(searchTerm, this.classes)
  }

  onClassOptionSelected (value: string): void {
    this.filters.class = value
    this.filters.subclass = ''
    this.filters.style = ''
    this.filteredSubclasses = []
    this.filteredStyles = []
    this.classLoading = true

    this.performanceDashboardAPIs
      .GetLowerPerformanceFilters({ product_id: value })
      .then(data => {
        if (data) {
          console.log('Loaded lower filters:', data)
          this.filteredSubclasses = data.products.map(product => product.product_id)
          this.subclasses = this.filteredSubclasses
        }
      })
      .finally(() => {
        this.classLoading = false
      })
  }

  // Subclass methods
  onSubclassInputChange (event: any): void {
    const searchTerm = event.target.value
    this.filteredSubclasses = this.filterOptions(searchTerm, this.subclasses)
  }

  onSubclassOptionSelected (value: string): void {
    this.filters.subclass = value
    this.filters.style = ''
    this.filteredStyles = []
    this.subclassLoading = true

    this.performanceDashboardAPIs
      .GetLowerPerformanceFilters({ product_id: value })
      .then(data => {
        if (data) {
          console.log('Loaded lower filters:', data)
          this.filteredStyles = data.products.map(product => product.product_id)
          this.styles = this.filteredStyles
        }
      })
      .finally(() => {
        this.subclassLoading = false
      })
  }

  // Style methods
  onStyleInputChange (event: any): void {
    const searchTerm = event.target.value
    this.filteredStyles = this.filterOptions(searchTerm, this.styles)
  }

  onStyleOptionSelected (value: string): void {
    this.filters.style = value
    this.styleLoading = true

    this.performanceDashboardAPIs
      .GetLowerPerformanceFilters({ product_id: value })
      .then(data => {
        if (data) {
          console.log('Loaded lower filters:', data)
          this.filteredStyleColors = data.products.map(product => product.product_id)
          this.styleColors = this.filteredStyleColors
        }
      })
      .finally(() => {
        this.styleLoading = false
      })
  }

  // Style-Color methods
  onStyleColorInputChange (event: any): void {
    const searchTerm = event.target.value
    this.filteredStyleColors = this.filterOptions(searchTerm, this.styleColors)
  }

  onStyleColorOptionSelected (value: string): void {
    this.filters.styleColor = value
  }

  // Channel methods
  onChannelInputChange (event: any): void {
    const searchTerm = event.target.value
    this.filteredChannels = this.filterOptions(searchTerm, this.channels)
  }

  onChannelOptionSelected (value: string): void {
    this.filters.channel = value
    this.filters.storeId = ''
    this.channelLoading = true

    this.performanceDashboardAPIs
      .GetLowerPerformanceFilters({ channel: value })
      .then(data => {
        if (data) {
          console.log('Loaded lower filters:', data)
          this.filteredStoreIds = data.storeIds
          this.storeIds = this.filteredStoreIds
          this.filters.customStoreList = ''
        }
      })
      .finally(() => {
        this.channelLoading = false
      })
  }

  // Custom Store List methods
  onCustomStoreListInputChange (event: any): void {
    const searchTerm = event.target.value
    this.filteredCustomStoreLists = this.filterOptions(searchTerm, this.customStoreLists)
  }

  onCustomStoreListOptionSelected (value: string): void {
    this.filters.customStoreList = value
    this.filters.storeId = ''
    this.customStoreListLoading = true

    this.performanceDashboardAPIs
      .GetLowerPerformanceFilters({ store_list: value })
      .then(data => {
        if (data) {
          console.log('Loaded lower filters:', data)
          this.filteredStoreIds = data.storeIds
          this.storeIds = this.filteredStoreIds
          this.filters.channel = ''
        }
      })
      .finally(() => {
        this.customStoreListLoading = false
      })
  }

  // Store ID methods
  onStoreIdInputChange (event: any): void {
    const searchTerm = event.target.value
    this.filteredStoreIds = this.filterOptions(searchTerm, this.storeIds)
  }

  onStoreIdOptionSelected (value: string): void {
    this.filters.storeId = value
  }

  // Season methods
  onSeasonInputChange (event: any): void {
    const searchTerm = event.target.value
    this.filteredSeasons = this.filterOptions(searchTerm, this.seasons)
  }

  onSeasonOptionSelected (value: string): void {
    this.filters.season = value
  }

  onSizeRangeInputChange (event: any): void {
    const searchTerm = event.target.value
    this.filteredSizeRanges = this.filterOptions(searchTerm, this.sizeRanges)
  }

  toggleSizeRange (size: string): void {
    const index = this.selectedSizeRanges.indexOf(size)

    if (index >= 0) {
      this.selectedSizeRanges.splice(index, 1)
    } else {
      this.selectedSizeRanges.push(size)
    }
    // Handle "All" selection logic
    if (size === 'All') {
      this.selectedSizeRanges = ['All']
    } else {
      const allIndex = this.selectedSizeRanges.indexOf('All')
      if (allIndex >= 0) {
        this.selectedSizeRanges.splice(allIndex, 1)
      }

      if (this.selectedSizeRanges.length === 0) {
        this.selectedSizeRanges = ['All']
      }
    }

    this.filters.sizeRange = this.selectedSizeRanges
    this.filters.selectedSizeRanges = this.selectedSizeRanges
  }

  get selectedSizeRangesDisplay (): string {
    return this.selectedSizeRanges?.length ? this.selectedSizeRanges.join(', ') : ''
  }

  applyFilters () {
    this.dashboardLoading = true
    if (this.exportButtonContainer && this.exportButtonContainer.parentNode) {
      this.pdfButtonVisible = false
      this.renderer.removeChild(this.exportButtonContainer.parentNode, this.exportButtonContainer)
      this.exportButtonContainer = null
    }
    console.log('Applying filters:', this.filters)

    this.getPerformanceData()
  }

  getPerformanceData () {
    const requestId = ++this.performanceDataRequestId
    this.performanceDashboardAPIs.GetPerformanceData({ filters: this.filters }).then(data => {
      // A newer request (Apply, Clear, etc.) has since been made -- discard
      // this response instead of letting a slow one land late and silently
      // overwrite the dashboard with data that no longer matches the filters
      // actually shown (and what would get exported to PDF).
      if (requestId !== this.performanceDataRequestId) {
        return
      }
      console.log('Got performance data:', this.filters)
      // Update data
      this.performanceData = data
      console.log('Loaded performance data:', this.performanceData)
      this.seasonalLostSalesData = {
        labels: this.performanceData.charts.seasonal_lost_sales_vs_overstock.labels,
        datasets: [
          {
            label: 'Lost Sales',
            data: this.performanceData.charts.seasonal_lost_sales_vs_overstock.lost_sales,
            backgroundColor: '#1d63a5',
            borderColor: '#1d63a5',
            borderWidth: 1
          },
          {
            label: 'Overstock',
            data: this.performanceData.charts.seasonal_lost_sales_vs_overstock.overstock,
            backgroundColor: '#333',
            borderColor: '#333',
            borderWidth: 1
          }
        ]
      }

      const profilePrecision = this.performanceData?.charts?.profile_vs_demand_precision
      const selectedRanges = this.selectedSizeRanges?.filter?.(r => r !== 'All') || []

      if (
        this.filters?.subclass &&
                selectedRanges.length > 0 &&
                profilePrecision != null &&
                typeof profilePrecision === 'object'
      ) {
        const returnedSizeRanges = Object.keys(profilePrecision)
        const colorPalette = ['#1d63a5', '#f50057', '#ff9800', '#43a047']
        if (returnedSizeRanges.length > colorPalette.length) {
          for (let i = 0; i < returnedSizeRanges.length - colorPalette.length; i++) {
            const color = GenerateRandomColor(colorPalette)
            colorPalette.push(color)
          }
        }

        const datasets = []
        let labels: string[] = []

        for (let i = 0; i < returnedSizeRanges.length; i++) {
          const sizeRange = returnedSizeRanges[i]
          const data = profilePrecision[sizeRange]

          if (labels.length === 0 && Array.isArray(data.labels)) {
            labels = data.labels
          }

          const color = colorPalette[i % colorPalette.length]
          // Demand uses a lighter tint of the same hue so it's distinguishable
          // by color too, not just dash pattern -- keeps size ranges grouped
          // by hue while making Profile vs Demand obvious at a glance.
          const demandColor = this.lightenColor(color, 0.45)

          datasets.push({
            label: `${sizeRange} - Profile`,
            data: data.profile,
            borderColor: color,
            backgroundColor: color,
            fill: false,
            tension: 0.3,
            borderDash: [],
            pointStyle: 'circle',
            pointRadius: 5,
            pointHoverRadius: 7,
            dataType: 'profile',
            sizeRangeName: sizeRange
          })

          datasets.push({
            label: `${sizeRange} - Demand`,
            data: data.demand,
            borderColor: demandColor,
            backgroundColor: demandColor,
            fill: false,
            tension: 0.3,
            borderDash: [5, 5],
            pointStyle: 'triangle',
            pointRadius: 6,
            pointHoverRadius: 8,
            dataType: 'demand',
            sizeRangeName: sizeRange
          })
        }

        this.profilePrecisionData = datasets.length > 0 ? { labels, datasets } : null
      } else {
        this.profilePrecisionData = null
      }
      this.sizeSellThroughData = this.performanceData.table || []
      console.log('Loaded performance data:', this.performanceData)
      if (!this.pdfButtonVisible) {
        this.addExportButton()
        this.pdfButtonVisible = true
      }
      this.dashboardLoading = false
    })
  }

  loadDashboardFilters () {
    this.loading = true
    this.performanceDashboardAPIs
      .GetPerformanceFilters()
      .then(data => {
        if (data) {
          console.log('Loaded filters:', data)
          this.filteredDepartments = data.departments.map(department => department.product_id)
          this.departments = this.filteredDepartments
          this.filteredChannels = data.channels
          this.channels = this.filteredChannels
          this.filteredSeasons = data.seasons
          this.seasons = this.filteredSeasons
          this.filters.season = this.seasons[this.seasons.length - 1]
          this.filteredSizeRanges = data.sizeRanges
          this.sizeRanges = this.filteredSizeRanges
          this.filteredCustomStoreLists = data.customStoreLists.map((store: any) => store.store_list)
          this.customStoreLists = this.filteredCustomStoreLists
          this.loading = false
        }
      })
      .finally(() => {
        this.getPerformanceData()
      })
  }

  openKPIDetail (kpiType: string, row_data: any): void {
    const dialogRef = this.dialog.open(KpiDetailModalComponent, {
      width: '29%',
      height: '95%',
      position: { right: '0' },
      data: { kpiType, row_data },
      panelClass: 'kpi-slide-dialog',
      autoFocus: false,
      disableClose: true
    })

    dialogRef.componentInstance.closeModal = () => {
      const dialogElement =
                document.querySelector('.kpi-slide-dialog .mat-mdc-dialog-container') ||
                document.querySelector('.kpi-slide-dialog .mat-dialog-container')
      const backdrop = document.querySelector('.cdk-overlay-backdrop')

      if (dialogElement) {
        dialogElement.classList.add('closing')
      }
      if (backdrop) {
        backdrop.classList.add('closing')
      }

      setTimeout(() => {
        dialogRef.close()
      }, 300)
    }

    dialogRef.afterClosed().subscribe(result => {
      console.log('KPI detail modal closed')
    })
  }

  openFullTable (): void {
    const dialogData: FullTableModalData = {
      title: 'Regular Price Sell-Through by Size (with Weeks OOS)',
      columnDefs: this.sizeSellThroughColumnDefs,
      rowData: this.sizeSellThroughData
    }

    const dialogRef = this.dialog.open(FullTableModalComponent, {
      width: '95%',
      height: '95%',
      position: { right: '0' },
      data: dialogData,
      panelClass: 'full-table-dialog',
      autoFocus: false,
      disableClose: true
    })

    dialogRef.componentInstance.closeModal = () => {
      const dialogElement =
                document.querySelector('.full-table-dialog .mat-mdc-dialog-container') ||
                document.querySelector('.full-table-dialog .mat-dialog-container')
      const backdrop = document.querySelector('.cdk-overlay-backdrop')

      if (dialogElement) dialogElement.classList.add('closing')
      if (backdrop) backdrop.classList.add('closing')

      setTimeout(() => dialogRef.close(), 300)
    }
  }

  openBarChartDetail (): void {
    const dialogRef = this.dialog.open(PerformanceDashboardBarchartComponent, {
      width: '95%',
      height: '85%',
      position: { right: '0' },
      data: {
        seasonalLostSalesData: this.seasonalLostSalesData,
        seasonalLostSalesOptions: this.seasonalLostSalesOptions
      },
      panelClass: 'performance-barchart-detail',
      autoFocus: false,
      disableClose: true
    })

    dialogRef.componentInstance.closeModal = () => {
      const dialogElement =
                document.querySelector('.performance-barchart-detail .mat-mdc-dialog-container') ||
                document.querySelector('.performance-barchart-detail .mat-dialog-container')
      const backdrop = document.querySelector('.cdk-overlay-backdrop')

      if (dialogElement) {
        dialogElement.classList.add('closing')
      }
      if (backdrop) {
        backdrop.classList.add('closing')
      }

      setTimeout(() => {
        dialogRef.close()
      }, 300)
    }

    dialogRef.afterClosed().subscribe(result => {
      console.log('Performance barchart detail modal closed')
    })
  }

  openLineChartDetail (): void {
    const dialogRef = this.dialog.open(PerformanceDashboardLinechartComponent, {
      width: '95%',
      height: '95%',
      position: { right: '0' },
      data: {
        profilePrecisionData: this.profilePrecisionData,
        profilePrecisionOptions: this.profilePrecisionOptions
      },
      panelClass: 'performance-linechart-detail',
      autoFocus: false,
      disableClose: true
    })

    dialogRef.componentInstance.closeModal = () => {
      const dialogElement =
                document.querySelector('.performance-linechart-detail .mat-mdc-dialog-container') ||
                document.querySelector('.performance-linechart-detail .mat-dialog-container')
      const backdrop = document.querySelector('.cdk-overlay-backdrop')

      if (dialogElement) {
        dialogElement.classList.add('closing')
      }
      if (backdrop) {
        backdrop.classList.add('closing')
      }

      setTimeout(() => {
        dialogRef.close()
      }, 300)
    }

    dialogRef.afterClosed().subscribe(result => {
      console.log('Performance linechart detail modal closed')
    })
  }

  onOpenDrillInFiltersModal (event) {
    // Open the modal
    this.ref = this.dialogService.open(DrillInFilteringComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: false,
      styleClass: 'large-modal',
      data: {
        componentParent: this,
        selectedStoreList: this.drillInFilters,
        storeLists: this.drillInFiltersStoreList
      }
    })

    this.ref.onClose.subscribe(async data => {
      if (data && 'updatedStoreLists' in data) {
        const { allLists } = data['updatedStoreLists']
        this.customStoreLists = allLists.map((store: any) => store.store_list)
        this.filteredCustomStoreLists = [...this.customStoreLists]
      }
    })
  }

  updateChartData () {
    console.log('Updating chart data based on filters...')
  }

  updateTableData () {
    console.log('Updating table data based on filters...')
  }

  exportToPDF (button?: HTMLButtonElement, spinner?: HTMLElement, buttonText?: any) {
    // Show spinner and disable button
    if (button && spinner) {
      this.renderer.setStyle(spinner, 'display', 'block')
      this.renderer.setProperty(button, 'disabled', true)
      this.renderer.setStyle(button, 'cursor', 'not-allowed')
      this.renderer.setStyle(button, 'opacity', '0.7')
    }

    console.log(this.filters)
    const pdf_data = {
      filters: this.filters,
      kpis: [
        {
          title: 'Inventory Efficiency',
          value: this.performanceData.kpis.inventory_efficiency.value,
          change: this.performanceData.kpis.inventory_efficiency.change
        },
        {
          title: 'Regular Price Sell-Through',
          value: this.performanceData.kpis.regular_price_sell_through.value,
          change: this.performanceData.kpis.regular_price_sell_through.change
        },
        {
          title: 'Profile Precision Index',
          value: this.performanceData.kpis.profile_precision_index.value,
          change: this.performanceData.kpis.profile_precision_index.change
        },
        {
          title: 'Pack Alignment Index',
          value: this.performanceData.kpis.pack_alignment_index.value,
          change: this.performanceData.kpis.pack_alignment_index.change
        },
        {
          title: 'Profile Acceptance',
          value: this.performanceData.kpis.profile_acceptance.value,
          change: this.performanceData.kpis.profile_acceptance.change
        }
      ],
      barChart: {
        title: 'Seasonal Lost Sales vs Overstock (trend)',
        labels: this.performanceData.charts.seasonal_lost_sales_vs_overstock.labels,
        datasets: [
          {
            label: 'Lost Sales',
            data: this.performanceData.charts.seasonal_lost_sales_vs_overstock.lost_sales,
            color: '#1d63a5'
          },
          {
            label: 'Overstock',
            data: this.performanceData.charts.seasonal_lost_sales_vs_overstock.overstock,
            color: '#333333'
          }
        ]
      },
      lineChart: {
        title: 'Profile vs Demand by Size',
        labels: this.profilePrecisionData?.labels,
        datasets: this.profilePrecisionData?.datasets
      },
      table: {
        headers: ['Size', 'Sell-Through %', 'Last Season %'],
        rows: this.sizeSellThroughData.map(item => [
          item.product_size,
          item.sell_through_pct,
          item.last_season_pct
        ])
      }
    }

    console.log('Exporting to PDF with data:', pdf_data)
    this.performanceDashboardAPIs
      .ExportPerformanceDashboardPdf({ dashboardData: pdf_data })
      .then(async signedUrl => {
        console.log('Received PDF export signedUrl:', signedUrl)

        const res = await fetch(signedUrl)
        const withoutQuery = signedUrl.split('?')[0]
        const filename = withoutQuery.substring(withoutQuery.lastIndexOf('/') + 1)

        if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`)

        const blob = await res.blob()
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.style.display = 'none'

        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      })
      .catch(error => {
        console.error('Error exporting PDF:', error)
      })
      .finally(() => {
        // Hide spinner and re-enable button
        if (button && spinner) {
          this.renderer.setStyle(spinner, 'display', 'none')
          this.renderer.setProperty(button, 'disabled', false)
          this.renderer.setStyle(button, 'cursor', 'pointer')
          this.renderer.setStyle(button, 'opacity', '1')
        }
      })
  }

  ngOnDestroy (): void {
    if (this.exportButtonContainer && this.exportButtonContainer.parentNode) {
      this.renderer.removeChild(this.exportButtonContainer.parentNode, this.exportButtonContainer)
      this.exportButtonContainer = null
    }

    const styleEl = document.getElementById('spinner-keyframes')
    if (styleEl && styleEl.parentNode) {
      styleEl.parentNode.removeChild(styleEl)
    }
  }
}
